/**
 * Mobile Phase 2, chunk 3: issues and checklists on the phone. Keys use the web's ['ops', ws, ...] prefix so the
 * desktop pages and the phone screens share one cache, and one invalidation refreshes both.
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ChecklistRunDetail, ChecklistValue, IncidentDetail, IncidentInput } from '@tradeoutlet/shared';
import { dataApi } from './client';

type Ws = string | null | undefined;
export const opsDataKeys = {
  all: (ws: Ws) => ['ops', ws ?? ''] as const,
  myIssues: (ws: Ws, status: 'open' | 'closed') => ['ops', ws ?? '', 'my-issues', status] as const,
  issue: (ws: Ws, id: string) => ['ops', ws ?? '', 'incident', id] as const,
  categories: (ws: Ws) => ['ops', ws ?? '', 'categories'] as const,
  myChecklists: (ws: Ws) => ['ops', ws ?? '', 'mine'] as const,
  run: (ws: Ws, id: string) => ['ops', ws ?? '', 'run', id] as const,
  reportForm: (ws: Ws, location: string) => ['scoped-forms', ws ?? '', 'incident', 'resolve', location] as const,
};
/** After any issue or checklist change: every ops list, plus Today and the nav badges. */
export function invalidateOps(qc: QueryClient, ws: Ws) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['ops', ws ?? ''] }), qc.invalidateQueries({ queryKey: ['wf', ws, 'me-today'] }),
    qc.invalidateQueries({ queryKey: ['nav-badges', ws ?? ''] }),
  ]);
}

// ------------------------------------------------------------------------------------------------ issues
/** Issues I reported, am assigned or follow ("Mine & followed"), open or closed. */
export function useMyIssues(ws: Ws, status: 'open' | 'closed' = 'open') {
  return useQuery({ queryKey: opsDataKeys.myIssues(ws, status), queryFn: () => dataApi().ops.incidents(ws!, { scope: 'mine', status, limit: 100 }), enabled: Boolean(ws) });
}
export function useIssue(ws: Ws, id: string) {
  return useQuery({ queryKey: opsDataKeys.issue(ws, id), queryFn: () => dataApi().ops.incident(ws!, id), enabled: Boolean(ws && id) });
}
/**
 * t276u: opening an issue marks it read for you (desktop and phone), and so does leaving it, so comments that
 * arrived while it was open don't bring it back as unread. The lists refresh; the open issue itself does not.
 */
export function useMarkIssueRead(ws: Ws, id: string, loaded: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!ws || !id || !loaded) return;
    const mark = () => Promise.resolve().then(() => dataApi().ops.setIncidentsRead(ws, [id], true))
      .then(() => qc.invalidateQueries({ queryKey: ['ops', ws], predicate: q => q.queryKey[2] !== 'incident' }))
      .catch(() => undefined);
    void mark();
    return () => { void mark(); };
  }, [ws, id, loaded, qc]);
}
export function useIssueCategories(ws: Ws, enabled = true) {
  return useQuery({ queryKey: opsDataKeys.categories(ws), queryFn: () => dataApi().ops.incidentCategories(ws!), enabled: Boolean(ws && enabled), staleTime: 5 * 60_000 });
}
/** The custom report form (#36) that applies at a building, if any. */
export function useIssueReportForm(ws: Ws, locationId: string | null | undefined) {
  return useQuery({ queryKey: opsDataKeys.reportForm(ws, locationId ?? ''), queryFn: () => dataApi().resolveScopedForms(ws!, 'incident', { location_id: locationId! }), enabled: Boolean(ws && locationId) });
}
export function useReportIssue(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: IncidentInput) => dataApi().ops.createIncident(ws!, b),
    onSuccess: d => { qc.setQueryData(opsDataKeys.issue(ws, d.id), d); void invalidateOps(qc, ws); },
  });
}
export function useUpdateIssue(ws: Ws, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Parameters<ReturnType<typeof dataApi>['ops']['updateIncident']>[2]) => dataApi().ops.updateIncident(ws!, id, b),
    onSuccess: (d: IncidentDetail) => { qc.setQueryData(opsDataKeys.issue(ws, id), d); void invalidateOps(qc, ws); },
  });
}

/** t565u: change an issue's due date; the server posts the change in the issue's discussion. */
export function useSetIssueDue(ws: Ws, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { resolve_due_at: string | null }) => dataApi().ops.setIncidentDue(ws!, id, b),
    onSuccess: (d: IncidentDetail) => { qc.setQueryData(opsDataKeys.issue(ws, id), d); void invalidateOps(qc, ws); },
  });
}

// ------------------------------------------------------------------------------------------------ checklists
/** t329u: `range` (UK days, inclusive) = My checklists due then; none = the usual yesterday-and-today list. Keys share the prefix, so invalidating it refreshes both. */
export function useMyChecklists(ws: Ws, range?: { from: string; to: string } | null) {
  return useQuery({ queryKey: range ? [...opsDataKeys.myChecklists(ws), range.from, range.to] : opsDataKeys.myChecklists(ws),
    queryFn: () => dataApi().ops.myChecklists(ws!, range ?? undefined), enabled: Boolean(ws), refetchInterval: 60_000 });
}
export function useChecklistRun(ws: Ws, id: string) {
  return useQuery({ queryKey: opsDataKeys.run(ws, id), queryFn: () => dataApi().ops.checklistRun(ws!, id), enabled: Boolean(ws && id) });
}
export interface AnswerInput { item_id: string; value?: ChecklistValue; note?: string | null; photo_ids?: string[] }
/** Save one row. The run in the cache is patched straight away so the progress bar moves without a refetch. */
export function useAnswerChecklist(ws: Ws, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ item_id, ...b }: AnswerInput) => dataApi().ops.answerChecklist(ws!, runId, item_id, b),
    onMutate: ({ item_id, value, note, photo_ids }) => {
      qc.setQueryData<ChecklistRunDetail>(opsDataKeys.run(ws, runId), r => r && patchAnswer(r, item_id, { value, note, photo_ids }));
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: opsDataKeys.run(ws, runId) }); void qc.invalidateQueries({ queryKey: opsDataKeys.myChecklists(ws) }); },
  });
}
/** Pure: the run with one answer replaced (or added). Exported for tests. */
export function patchAnswer(r: ChecklistRunDetail, itemId: string, p: { value?: ChecklistValue; note?: string | null; photo_ids?: string[] }): ChecklistRunDetail {
  const cur = r.answers.find(a => a.item_id === itemId);
  const next = { ...(cur ?? { item_id: itemId, value: null, note: null, photo_ids: [], failed: false, follow_up_type: null, follow_up_id: null, answered_by: '', answered_by_name: '', answered_at: new Date().toISOString() }),
    ...(p.value !== undefined ? { value: p.value } : {}), ...(p.note !== undefined ? { note: p.note } : {}), ...(p.photo_ids ? { photo_ids: p.photo_ids } : {}) };
  return { ...r, answers: cur ? r.answers.map(a => (a.item_id === itemId ? next : a)) : [...r.answers, next] };
}
export function useRemoveChecklistPhoto(ws: Ws, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ item_id, att }: { item_id: string; att: string }) => dataApi().ops.removeChecklistPhoto(ws!, runId, item_id, att),
    onSuccess: r => { qc.setQueryData<ChecklistRunDetail>(opsDataKeys.run(ws, runId), d => d && patchAnswer(d, r.item_id, { photo_ids: r.photo_ids })); void qc.invalidateQueries({ queryKey: opsDataKeys.run(ws, runId) }); },
  });
}
export function useCompleteChecklist(ws: Ws, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signature_name: string | null) => dataApi().ops.completeChecklist(ws!, runId, { signature_name }),
    onSuccess: d => { qc.setQueryData(opsDataKeys.run(ws, runId), d); void invalidateOps(qc, ws); },
  });
}
